import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { BiContainer, Column, Row, TextButton, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { useOpportunityAction } from '../hooks/useOpportunityAction';
import { ACTION_BI_NAME_BY_KIND, PULSE_OPPORTUNITY_ROW_BI_NAME } from '../PulseOpportunitiesWidget.constants';
import { classifyConfidence } from '../utils/classifyConfidence';
import { formatMoney } from '../utils/formatOpportunityValues';
import { actionLabel, confidenceLabel, kindLabel } from '../utils/opportunityLabels';
import { opportunityEvidenceLines } from './evidence/opportunityEvidenceLines';
export const GetOpportunityRowTID = (parentTestId)=>getTestIds(parentTestId);
export const OpportunityRow = (param)=>{
    let { opportunity, clientLabel, locale, position, feedId, testId } = param;
    const { OpportunityRowTestIds } = useMemo(()=>GetOpportunityRowTID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys('ai.pulse');
    const evidenceLines = opportunityEvidenceLines(opportunity, texts.evidence, locale);
    const { activate, isChecking, isUnreachable } = useOpportunityAction({
        opportunity,
        feedId
    });
    const label = actionLabel(opportunity.kind, texts.action);
    const humanReadablePosition = position + 1;
    return /*#__PURE__*/ _jsx(BiContainer, {
        name: PULSE_OPPORTUNITY_ROW_BI_NAME,
        data: {
            kind: opportunity.kind,
            position
        },
        children: /*#__PURE__*/ _jsxs(Column, {
            testId: OpportunityRowTestIds.Root(),
            gap: 4,
            children: [
                /*#__PURE__*/ _jsxs(Row, {
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 8,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            weight: "normal",
                            testId: OpportunityRowTestIds.Subject(),
                            children: opportunity.subject
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            weight: "normal",
                            testId: OpportunityRowTestIds.Impact(),
                            children: formatMoney(opportunity.estAnnualImpact, locale)
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    wrap: true,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "secondary",
                            testId: OpportunityRowTestIds.Kind(),
                            children: kindLabel(opportunity.kind, texts.kind)
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "secondary",
                            testId: OpportunityRowTestIds.Client(),
                            children: clientLabel
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "secondary",
                            testId: OpportunityRowTestIds.Confidence(),
                            children: confidenceLabel(classifyConfidence(opportunity.confidence), texts.confidence)
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Column, {
                    gap: 2,
                    children: evidenceLines.map((line, index)=>/*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "secondary",
                            testId: OpportunityRowTestIds.EvidenceLine(index),
                            children: line
                        }, index))
                }),
                /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    wrap: true,
                    children: [
                        /*#__PURE__*/ _jsx(TextButton, {
                            bi: {
                                name: ACTION_BI_NAME_BY_KIND[opportunity.kind]
                            },
                            testId: OpportunityRowTestIds.Action(),
                            "aria-label": texts.action.accessibleName({
                                action: label,
                                subject: opportunity.subject,
                                position: humanReadablePosition
                            }),
                            disabled: isChecking || isUnreachable,
                            onClick: ()=>void activate(),
                            children: label
                        }),
                        isUnreachable && /*#__PURE__*/ _jsx(Typography, {
                            role: "alert",
                            variant: "paragraph3",
                            skin: "error",
                            testId: OpportunityRowTestIds.Unavailable(),
                            children: texts.action.unavailable()
                        })
                    ]
                })
            ]
        })
    });
};
